import { Component, OnInit, AfterViewInit, Input, Output, EventEmitter, ViewContainerRef, Renderer2, Type as AngularCoreType, ViewChild, ElementRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormBuilder, FormGroup, FormControl, Validators, ValidatorFn } from '@angular/forms'; // Used for Reactive Forms
import { ControlType, ValidationInfo, ValidationMessageInfo, FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { FormFieldPropertyEnum } from '../../../models/form-builder/form-field-property-enum.model';
import { FormFieldMode } from '../form-field-mode.enum';
import { FormField } from '../../../models/form-builder/form-field.model';
import { FormInstanceElement } from '../../../models/form-builder/form-instance-element.model';
import { CurrentUser } from '../../../../security/current-user.model';
import { TextInputFormFieldBaseComponent } from '../input-form-field-base/text-input-form-field-base.component';
import { FormFieldOnInitPropertyEnum } from '../../../models/form-builder/form-field-on-init-output-property.enum';
import { IGridRow } from '../../../interfaces/grid-row.interface';
import { FormFieldProcessingPhaseEnum } from '../../../enums/form-field-processing-phase.enum';
import { LastEditedDateFieldType } from '../../../models/form-builder/form-field-types';
import { LastEditedDateFieldDefLogic } from '../../../models/form-builder/field-defs/last-edited-date-field-def-logic';
import { DatePipe } from '@angular/common';


// Note:  please note the 'providers' definition below, as it is needed.
//        Without it, you will get the following exception:
//
//             No value accessor for form control with unspecified name
//
// The above exception gets thrown when a component, in this case our
// base class, implements interface 'ControlValueAccessor' and does not
// provide the 'providers' definition below.  Implementing the
// 'ControlValueAccessor' interface allows a form field component to
// support [(ngMode)], so users of the component can use [(ngModel)].
@Component({
    selector: 'app-last-edited-date-form-field',
    templateUrl: './last-edited-date-form-field.component.html',
    styleUrls: ['./last-edited-date-form-field.component.scss', '../form-fields.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: LastEditedDateFormFieldComponent,
            multi: true
        }
    ],
    standalone: false
})
export class LastEditedDateFormFieldComponent extends TextInputFormFieldBaseComponent implements OnInit, AfterViewInit {
    // Properties
    // Note:  several properties are implemented in my base class.
    @Output() onInit = new EventEmitter();
    @ViewChild('gridOnlyTextArea') gridOnlyTextAreaElement: ElementRef;
    private currentUser: CurrentUser;
    /**
     * User configurable properties that appear in the drawer for this field
     */
    private readonly formFieldProperties: string[] =
        [
            FormFieldPropertyEnum.NAME,
            FormFieldPropertyEnum.DISPLAY_NAME,
            FormFieldPropertyEnum.GRID_COLUMN_WIDTH,
            FormFieldPropertyEnum.BLANK_VALUE,
            FormFieldPropertyEnum.TOOL_TIP,
            FormFieldPropertyEnum.MAX_LENGTH
        ];

    private readonly validationOptions: ValidationInfo[] =
        [
            {
                DISPLAY_NAME: 'None',
                IS_CUSTOM_REGULAR_EXPRESSION: false,
                REGULAR_EXPRESSION: null,
                ERROR_MESSAGE: null
            },
        ];

    public constructor(private rendererParam: Renderer2) {
        super(rendererParam);
        this.matInputId = this.generateUniqueId('lastEditedDate');

        return;
    }

    // Life cycle methods.
    public ngOnInit(): void {

        // Set a default constraint value.
        if (this.FormField) {
            if (!this.FormField.constraintName) {
                this.FormField.constraintName = "None";
            }
        }

        let hshOnInitProperties = this.getProperties();
        this.onInit.emit(hshOnInitProperties);

        return;
    }

    public ngAfterViewInit(): void {
        // Note:  this is where a @ViewChild() definition should be resolved.
    }

    // Implement abstract methods.
    public getProperties(): any {
        let validationNames: string[] = [];
        for (let iValidation: number = 0; iValidation < this.validationOptions.length; iValidation++) {
            let validationInfo: ValidationInfo = this.validationOptions[iValidation];

            validationNames.push(validationInfo.DISPLAY_NAME);
        }

        let hshOnInitProperties = {
            component: this,
            formField: this.FormField,
            properties: this.formFieldProperties,
            constraintNames: validationNames,
            customRegexConstraintName: 'Custom Regular Expression'
        };
        hshOnInitProperties[FormFieldOnInitPropertyEnum.REQUIRED_PREVIEW_INSTANCE_MODE_HEIGHT] = 190;
        hshOnInitProperties[FormFieldOnInitPropertyEnum.REQUIRED_PREVIEW_INSTANCE_MODE_HEIGHT_UNIT] = 'px';

        return hshOnInitProperties;
    }

    // Override the method used to get my class.
    public getFormFieldClass(): AngularCoreType<any> {
        return (LastEditedDateFormFieldComponent);
    }

    public get canHaveFieldConditionalLogic(): boolean {
        return false;
    }

    //**Display the last edited date*/
    public get LastEditedDate(): string {
        let currentDate: Date = new Date();
        const datePipe = new DatePipe('en-US');
        const formattedDate = datePipe.transform(currentDate, 'yyyy-MM-dd HH:mm:ss') || '';

        return formattedDate.toString();
    }

    public get ReadOnly(): boolean {
        /***This field should always be Readonly*/
        let bResult: boolean = true;

        return bResult;
    }

    protected setDefaultTextValue(): void {
        //***No Default Value will be used outside of the current user*/
    }

    // Initializes/reinitializes the component and its FormControl (formerly named writeValueTriggered)
    protected initalizeComponentFormControl(): void {
        if ((this.Mode === 'preview') || (this.Mode === 'instance')) {
            if (this.ControlType === ControlType.REACTIVE_FORMS) {
                this.SetupFormControl();
            }
        }

        //***When grid row enters edit mode, display the current date & time*/
        let fieldType: string = LastEditedDateFieldType;
        if (this.FormField.fieldDefinitionClassName == fieldType && this.FormControl)
            this.FormControl.setValue(this.LastEditedDate);

        return;
    }

    // Override the getDisplayValue() base class method.
    // Define a method that allows a component to return its display value.
    public pseudoStatic_getDisplayValue(formFieldParam: FormField, formInstanceElementParam: FormInstanceElement, gridRow: IGridRow, processingPhase: FormFieldProcessingPhaseEnum): string {
        if ((!formInstanceElementParam.UserUpdatedData) ||
            (!formInstanceElementParam.textValue)) {
            // Set a default value.
            formInstanceElementParam.TextValue = '';
        }

        return (formInstanceElementParam.textValue);
    }

    // Override
    protected buildValidatorFunctionsAndMessages(
        arrValidatorsParam: ValidatorFn[],
        arrValidationMessagesParam: ValidationMessageInfo[]): void {
        // First call super.
        super.buildValidatorFunctionsAndMessages(arrValidatorsParam, arrValidationMessagesParam);

        // If I have a selected validation format, apply it here.
        // Note:  temporarily using display format as an optional validation format.
        if (this.FormField.constraintName && (this.FormField.constraintName.trim() !== '')) {
            let arrValidationOption: ValidationInfo[] = this.validationOptions.filter(vi => vi.DISPLAY_NAME === this.FormField.constraintName);

            if (arrValidationOption && (arrValidationOption.length === 1)) {
                let validationOption: ValidationInfo = arrValidationOption[0];

                // Don't apply both a named validation and a regular expression.
                // A this.FormField.regex expression would have been applied in
                // the above call to the base class's method.
                if (!this.FormField.regex) {
                    arrValidatorsParam.push(Validators.pattern(validationOption.REGULAR_EXPRESSION));

                    arrValidationMessagesParam.push({ type: 'pattern', message: `${this.DisplayName} ${validationOption.ERROR_MESSAGE}` });
                }
            }
        }

        // Done.
        return;
    }
}
